// 화면 품질·FPS(설계서 §9.5) — 세션 화면의 **선택·저장·자동 조절** 로직(SessionView 는 이 훅만 부른다).
//
// 세 가지를 한 자리에 모은다:
//  1) 수동 선택 → `ctrl` 채널로 요청 → 적용 결과 표시
//  2) 고른 값을 **뷰어 기본값으로 저장**(localStorage) → 다음 접속에 자동 적용. 서버·DB 는 손대지 않는다.
//  3) 자동 모드 — 판정은 `auto.ts` 의 순수 함수가 하고, 이 훅은 표본을 먹이고 결과를 채널로 내보낸다.
//     🔴 자동이 내린 결정도 **수동과 똑같은 `q` 메시지**로 나간다(에이전트는 자동인지 모른다).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '../sessions/types';
import {
  initialAutoState,
  ladderLabel,
  levelOf,
  step,
  type AutoState,
} from './auto';
import {
  DEFAULT_FPS,
  DEFAULT_PRESET,
  encodedDims,
  resolutionCapped,
  type PresetId,
  type QualityApplied,
  type QualitySpec,
} from './protocol';

/** 뷰어 기본값 저장 키(이 브라우저·이 앱에만). 서버에는 저장하지 않는다. */
const PREF_KEY = 'rs.quality.pref';

interface StoredPref {
  preset: PresetId;
  fps: number;
  auto: boolean;
}

/**
 * 저장된 기본값을 읽는다. 없거나 깨졌으면 현행 기본값(보통/15/수동) — 🔴 저장값이 이상하다고 UI 를
 * 막지 않는다. 브라우저 저장소는 사생활 모드·정책으로 **던질 수도** 있어 전부 try 로 감싼다.
 */
export function loadPref(): StoredPref {
  const fallback: StoredPref = { preset: DEFAULT_PRESET, fps: DEFAULT_FPS, auto: false };
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (!raw) {
      return fallback;
    }
    const v = JSON.parse(raw) as Partial<StoredPref>;
    return {
      preset: v.preset === 'low' || v.preset === 'med' || v.preset === 'high' ? v.preset : fallback.preset,
      fps: typeof v.fps === 'number' ? v.fps : fallback.fps,
      auto: v.auto === true,
    };
  } catch {
    return fallback;
  }
}

function savePref(p: StoredPref): void {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(p));
  } catch {
    // 저장 실패는 조용히 넘어간다(이번 세션에는 이미 적용됐다).
  }
}

export interface QualityView {
  /** 이 세션에서 화질을 바꿀 수 있는가(에이전트 ctrl 채널이 있는가). 구버전 에이전트면 false. */
  available: boolean;
  /** 지금 고른 값(요청 기준). */
  spec: QualitySpec;
  /** 에이전트가 **실제로 적용한** 결과(아직 없으면 null). */
  applied: QualityApplied | null;
  /** 자동 모드인가. */
  auto: boolean;
  /** 자동 모드가 지금 서 있는 사다리 칸 문구(표시용). */
  autoLabel: string;
  /** 마지막 오류 문구(없으면 null). */
  error: string | null;
  /**
   * 이 화면에서 프리셋을 올려도 **해상도는 오르지 않는다**(원본이 이미 상한 이하).
   * UI 가 이걸 말해 주지 않으면 "높음으로 바꿨는데 왜 그대로냐" 가 된다.
   */
  resolutionCapped: boolean;
  /** 지금 고른 값에서 예상되는 인코딩 치수(원본을 알 때만). */
  expectedDims: { width: number; height: number } | null;
  setPreset(p: PresetId): void;
  setFps(f: number): void;
  setAuto(on: boolean): void;
}

export function useQuality(session: Session): QualityView {
  const pref = useRef<StoredPref>(loadPref());
  const [spec, setSpec] = useState<QualitySpec>({ preset: pref.current.preset, fps: pref.current.fps });
  const [auto, setAutoState] = useState(pref.current.auto);
  const autoRef = useRef<AutoState>(initialAutoState(levelOf(spec)));
  const caps = session.qualityCaps;
  const channel = session.client.quality;
  const available = caps != null && channel != null;
  // 적용 결과·오류는 세션 상태로 온다(clipCaps·fileCaps 와 같은 경로 — 배선이 한 곳에 모인다).
  const applied = session.qualityApplied ?? null;
  const error = session.qualityError ?? null;

  /** 채널로 요청을 내보낸다(수동·자동 공통 경로). */
  const push = useCallback(
    (next: QualitySpec) => {
      setSpec(next);
      session.client.quality?.set(next);
    },
    [session.client],
  );

  // 채널이 열리면 **저장된 기본값**을 한 번 적용한다(에이전트는 늘 기본 프리셋으로 시작한다).
  const didApplyPref = useRef(false);
  useEffect(() => {
    if (!available || didApplyPref.current) {
      return;
    }
    didApplyPref.current = true;
    const want = { preset: pref.current.preset, fps: pref.current.fps };
    // 에이전트가 이미 그 값이면 보내지 않는다(불필요한 인코더 재생성).
    if (caps && (caps.current.preset !== want.preset || caps.current.fps !== want.fps)) {
      push(want);
    } else {
      setSpec(want);
    }
    autoRef.current = initialAutoState(levelOf(want));
  }, [available, caps, push]);

  // ── 자동 조절 — 통계 표본이 올 때마다 판정한다 ──
  const stats = session.stats;
  useEffect(() => {
    if (!auto || !available || !stats) {
      return;
    }
    const d = step(autoRef.current, {
      lossPct: stats.lossPct,
      fpsNum: stats.fpsNum,
      targetFps: spec.fps,
      tMs: Date.now(),
    });
    autoRef.current = d.state;
    if (d.next) {
      push(d.next);
    }
    // stats 는 1초마다 새 객체로 온다 → 이 효과가 표본마다 한 번 돈다.
  }, [stats, auto, available, spec.fps, push]);

  const setPreset = useCallback(
    (p: PresetId) => {
      // 🔴 사용자가 직접 고르면 자동은 해제된다(사람의 의사가 항상 이긴다).
      setAutoState(false);
      const next = { preset: p, fps: spec.fps };
      pref.current = { ...next, auto: false };
      savePref(pref.current);
      autoRef.current = initialAutoState(levelOf(next));
      push(next);
    },
    [spec.fps, push],
  );

  const setFps = useCallback(
    (f: number) => {
      setAutoState(false);
      const next = { preset: spec.preset, fps: f };
      pref.current = { ...next, auto: false };
      savePref(pref.current);
      autoRef.current = initialAutoState(levelOf(next));
      push(next);
    },
    [spec.preset, push],
  );

  const setAuto = useCallback(
    (on: boolean) => {
      setAutoState(on);
      pref.current = { ...pref.current, auto: on };
      savePref(pref.current);
      // 자동을 켜면 **지금 값에서 이어서** 시작한다(갑자기 기본값으로 튀지 않게).
      autoRef.current = initialAutoState(levelOf(spec));
    },
    [spec],
  );

  const native = caps?.native;
  return {
    available,
    spec,
    applied,
    auto,
    autoLabel: ladderLabel(autoRef.current.level),
    error,
    resolutionCapped: native ? resolutionCapped(spec.preset, native.width) : false,
    expectedDims: native ? encodedDims(spec.preset, native.width, native.height) : null,
    setPreset,
    setFps,
    setAuto,
  };
}
